import { supabase } from '@lib/supabase';
import type { Tables, InsertTables } from '@core/types/database.types';

export type Notification = Tables<'notificaciones'>;
export type NewNotification = InsertTables<'notificaciones'>;

export type NotificationType = 
  | 'cita_asignada'
  | 'cita_cancelada'
  | 'cita_completada'
  | 'cita_recordatorio'
  | 'mensaje_admin'
  | 'actualizacion_sistema';

export type NotificationPriority = 'baja' | 'media' | 'alta' | 'urgente';

/**
 * Servicio para gestión de notificaciones in-app
 */
export const notificationsService = {
  /**
   * Obtener notificaciones del usuario actual
   */
  async getMyNotifications(limit = 20, includeRead = false) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('No autenticado');

    let query = supabase
      .from('notificaciones')
      .select('*')
      .eq('usuario_id', user.id)
      .order('fecha_creacion', { ascending: false })
      .limit(limit);

    if (!includeRead) {
      query = query.eq('leida', false);
    }

    const { data, error } = await query;

    if (error) throw new Error(error.message);
    return data || [];
  },

  /**
   * Obtener cantidad de notificaciones no leídas
   */
  async getUnreadCount(): Promise<number> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return 0;

    const { count, error } = await supabase
      .from('notificaciones')
      .select('*', { count: 'exact', head: true })
      .eq('usuario_id', user.id)
      .eq('leida', false);

    if (error) {
      console.error('Error getting unread count:', error);
      return 0;
    }

    return count || 0;
  },

  /**
   * Marcar notificación como leída
   */
  async markAsRead(notificationId: string) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('No autenticado');

    const { error } = await supabase
      .from('notificaciones')
      .update({ leida: true })
      .eq('id', notificationId)
      .eq('usuario_id', user.id);

    if (error) throw new Error(error.message);
  },

  /**
   * Marcar todas las notificaciones como leídas
   */
  async markAllAsRead() {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('No autenticado');

    const { error } = await supabase
      .from('notificaciones')
      .update({ leida: true })
      .eq('usuario_id', user.id)
      .eq('leida', false);

    if (error) throw new Error(error.message);
  },

  /**
   * Eliminar notificación
   */
  async delete(notificationId: string) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('No autenticado');

    const { error } = await supabase
      .from('notificaciones')
      .delete()
      .eq('id', notificationId)
      .eq('usuario_id', user.id);

    if (error) throw new Error(error.message);
  },

  /**
   * Crear notificación (generalmente desde backend/triggers)
   * Solo puede ser llamado por admins/super_admin/supervisor_tecnico — el rol
   * tecnico recibiría 403 por la política RLS de INSERT.
   */
  async create(notification: NewNotification) {
    const { data, error } = await supabase
      .from('notificaciones')
      .insert(notification)
      .select()
      .maybeSingle();

    if (error) throw new Error(error.message);
    if (!data) throw new Error('No se pudo crear la notificación.');
    return data;
  },

  /**
   * Suscribirse a notificaciones en tiempo real
   */
  subscribeToNotifications(userId: string, callback: (notification: Notification) => void) {
    const channel = supabase
      .channel('notifications')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notificaciones',
          filter: `usuario_id=eq.${userId}`,
        },
        (payload) => {
          callback(payload.new as Notification);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  },
};

